import React from 'react';
import { MarkdownRenderer } from "../MarkdownRenderer";
import { DefaultToolCallRenderer } from '../ToolRenderers';
import { TraceFlameGraph } from "../../SidePanel/Trace/TraceFlameGraph";
import { Trace, SpanAttributes, ResourceAttributes } from "../../../pages/Traces";

// Message interface for the TraceFlameGraphToolRenderer
interface Message {
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
    tool_name?: string;
    tool_call?: boolean;
    tool_call_id?: string;
    tool_call_function_name?: string;
    tool_call_function_args?: string;
    tool_call_response_id?: string;
    tool_call_response_content?: string;
    screenshot?: string;
}

// ToolRendererProps interface
export interface ToolRendererProps {
    message: Message;
    isToolCall: boolean;
}

// API response span structure
interface ApiSpan {
    time: number;
    duration: number;
    traceId: string;
    spanId: string;
    parentSpanId: string;
    spanAttributes: Record<string, string>;
    resourceAttributes: Record<string, string>;
    serviceName: string;
    spanName: string;
    links?: any[]; // Optional, not used in this renderer
}

// Response structure
interface SpansForTraceResponse {
    traces: ApiSpan[];
}

// Tool renderer for trace flame graph
export function TraceFlameGraphToolRenderer({ message, isToolCall }: ToolRendererProps) {
    // If this is just the tool call and not the response, render the default tool call renderer
    if (!message.tool_call_response_content) {
        return <DefaultToolCallRenderer message={message} />;
    }

    let response: SpansForTraceResponse | null = null;
    try {
        response = JSON.parse(message.tool_call_response_content);
    } catch (error) {
        console.error('Error parsing trace span response data:', error);
        return null;
    }

    // Validate response
    if (!response || !response.traces || !Array.isArray(response.traces) || response.traces.length === 0) {
        return null;
    }

    // Convert API response spans to Trace objects expected by TraceFlameGraph
    const enhancedSpans = response.traces.map(span => {
        // Determine status code
        let statusCode = "STATUS_CODE_UNSET";
        if (span.spanAttributes && span.spanAttributes["http.status_code"]) {
            const httpStatus = span.spanAttributes["http.status_code"];
            statusCode = httpStatus === "200" ? "STATUS_CODE_OK" :
                        (Number(httpStatus) >= 400) ? "STATUS_CODE_ERROR" : "STATUS_CODE_UNSET";
        }

        // Cast attributes to unknown first and then to their expected types
        // This is a workaround for TypeScript's strict type checking
        const spanAttributesTyped = span.spanAttributes as unknown as SpanAttributes;
        const resourceAttributesTyped = span.resourceAttributes as unknown as ResourceAttributes;

        return {
            // Fields directly mapped from API
            traceId: span.traceId,
            spanId: span.spanId,
            parentSpanId: span.parentSpanId,
            time: span.time,
            duration: span.duration,
            spanName: span.spanName,
            serviceName: span.serviceName,
            resourceAttributes: resourceAttributesTyped,
            spanAttributes: spanAttributesTyped,
            links: span.links || [], // Optional, not used in this renderer

            // Required fields by TraceFlameGraph that we need to derive
            displayServiceName: span.serviceName,
            clientName: span.spanAttributes["net.peer.name"] || "",
            displayClientName: span.spanAttributes["net.peer.name"] || "",
            statusCode
        };
    });

    // Get traceId from the first span (all spans should have the same traceId)
    const traceId = enhancedSpans[0]?.traceId || '';

    return (
        <div className="w-full">
            <MarkdownRenderer content={message.content} />

            <div className="border rounded overflow-hidden bg-[#131B2E]/30 mt-2" style={{ height: '500px' }}>
                <TraceFlameGraph spans={enhancedSpans} traceId={traceId} />
            </div>
        </div>
    );
}

// Static method to determine if this renderer should render a message
TraceFlameGraphToolRenderer.shouldRender = (message: Message): boolean => {
    return message.tool_name === "get_trace_spans";
};